import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { citiesData } from '../mockData';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookOnline = () => {
  const { toast } = useToast();
  const [date, setDate] = useState();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: '',
    pickupTime: '',
    pickupLocation: '',
    dropoffLocation: '',
    city: '',
    zipCode: '',
    state: 'IL',
    passengers: '',
    luggage: '',
    notes: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceTypeChange = (value) => {
    setFormData(prev => {
      const updates = { serviceType: value };
      
      // Autofill based on service type
      if (value === 'from-airport') {
        updates.pickupLocation = "O'Hare International Airport (ORD)";
        updates.dropoffLocation = '';
      } else if (value === 'to-airport') {
        updates.pickupLocation = '';
        updates.dropoffLocation = "O'Hare International Airport (ORD)";
      }
      
      return { ...prev, ...updates };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.phone || !formData.email ||
        !formData.serviceType || !date || !formData.pickupTime || 
        !formData.pickupLocation || !formData.dropoffLocation || 
        !formData.city || !formData.zipCode || !formData.state) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Submit booking to backend
      const response = await axios.post(`${API}/bookings`, {
        ...formData,
        pickupDate: format(date, 'yyyy-MM-dd')
      });

      if (response.data.success) {
        // Show full-page confirmation
        setShowConfirmation(true);

        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: '',
          serviceType: '',
          pickupTime: '',
          pickupLocation: '',
          dropoffLocation: '',
          city: '',
          zipCode: '',
          state: 'IL',
          passengers: '',
          luggage: '',
          notes: ''
        });
        setDate(undefined);
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: "Booking Failed",
        description: "There was an error submitting your booking. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Show confirmation page if booking was successful
  if (showConfirmation) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl shadow-2xl p-12">
            {/* Success Icon */}
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Request Submitted!
            </h1>
            
            <p className="text-xl text-gray-600 mb-8">
              Thank you for choosing LiveryRUs. Your booking request has been received and a representative will contact you shortly to confirm your reservation.
            </p>
            
            <Button
              onClick={() => setShowConfirmation(false)}
              className="bg-[#00D4FF] hover:bg-[#00B8E6] text-black font-bold px-8 py-3 text-lg"
            >
              Submit Another Request
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Hero Section */}
      <section className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">Request Your Ride</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Reserve your luxury airport transportation in just a few simple steps.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Information - First */}
                <div>
                  <Label htmlFor="name" className="text-base font-semibold mb-2 block">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder=""
                    className="h-12"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-base font-semibold mb-2 block">
                      Phone Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder=""
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-base font-semibold mb-2 block">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder=""
                      className="h-12"
                    />
                  </div>
                </div>

                {/* Service Type */}
                <div>
                  <Label htmlFor="serviceType" className="text-base font-semibold mb-2 block">
                    Service Type <span className="text-red-500">*</span>
                  </Label>
                  <Select value={formData.serviceType} onValueChange={handleServiceTypeChange}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="from-airport">From O'Hare Airport</SelectItem>
                      <SelectItem value="to-airport">To O'Hare Airport</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date and Time Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-semibold mb-2 block">
                      Pickup Date <span className="text-red-500">*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full h-12 justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, 'PPP') : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label htmlFor="pickupTime" className="text-base font-semibold mb-2 block">
                      Pickup Time <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <Input
                        id="pickupTime"
                        type="time"
                        value={formData.pickupTime}
                        onChange={(e) => handleInputChange('pickupTime', e.target.value)}
                        className="h-12 pl-10"
                      />
                    </div>
                  </div>
                </div>

                {/* Locations - Order changes based on service type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {formData.serviceType === 'from-airport' ? (
                    <>
                      {/* From Airport: Pickup (ORD) first */}
                      <div>
                        <Label htmlFor="pickupLocation" className="text-base font-semibold mb-2 block">
                          Pickup Address <span className="text-red-500">*</span>
                        </Label>
                        <div className="h-12 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 flex items-center text-gray-600">
                          O'Hare International Airport (ORD)
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="dropoffLocation" className="text-base font-semibold mb-2 block">
                          Dropoff Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="dropoffLocation"
                          type="text"
                          value={formData.dropoffLocation}
                          onChange={(e) => handleInputChange('dropoffLocation', e.target.value)}
                          placeholder="Enter full dropoff address"
                          className="h-12"
                        />
                      </div>
                    </>
                  ) : formData.serviceType === 'to-airport' ? (
                    <>
                      {/* To Airport: Dropoff (ORD) first */}
                      <div>
                        <Label htmlFor="dropoffLocation" className="text-base font-semibold mb-2 block">
                          Dropoff Address <span className="text-red-500">*</span>
                        </Label>
                        <div className="h-12 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 flex items-center text-gray-600">
                          O'Hare International Airport (ORD)
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="pickupLocation" className="text-base font-semibold mb-2 block">
                          Pickup Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="pickupLocation"
                          type="text"
                          value={formData.pickupLocation}
                          onChange={(e) => handleInputChange('pickupLocation', e.target.value)}
                          placeholder="Enter full pickup address"
                          className="h-12"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* No service type selected yet */}
                      <div>
                        <Label htmlFor="pickupLocation" className="text-base font-semibold mb-2 block">
                          Pickup Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="pickupLocation"
                          type="text"
                          value={formData.pickupLocation}
                          onChange={(e) => handleInputChange('pickupLocation', e.target.value)}
                          placeholder="Select service type first"
                          className="h-12"
                          disabled
                        />
                      </div>
                      <div>
                        <Label htmlFor="dropoffLocation" className="text-base font-semibold mb-2 block">
                          Dropoff Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="dropoffLocation"
                          type="text"
                          value={formData.dropoffLocation}
                          onChange={(e) => handleInputChange('dropoffLocation', e.target.value)}
                          placeholder="Select service type first"
                          className="h-12"
                          disabled
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* City, Zip Code, and State */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="city" className="text-base font-semibold mb-2 block">
                      City <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="Enter city"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="zipCode" className="text-base font-semibold mb-2 block">
                      Zip Code <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="zipCode"
                      type="text"
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      placeholder="Enter zip code"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="state" className="text-base font-semibold mb-2 block">
                      State <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="state"
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className="h-12"
                      disabled
                    />
                  </div>
                </div>

                {/* Passengers and Luggage */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="passengers" className="text-base font-semibold mb-2 block">
                      Number of Passengers
                    </Label>
                    <Select value={formData.passengers} onValueChange={(value) => handleInputChange('passengers', value)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select passengers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Passenger</SelectItem>
                        <SelectItem value="2">2 Passengers</SelectItem>
                        <SelectItem value="3">3 Passengers</SelectItem>
                        <SelectItem value="4">4 Passengers</SelectItem>
                        <SelectItem value="5">5 Passengers</SelectItem>
                        <SelectItem value="6">6 Passengers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="luggage" className="text-base font-semibold mb-2 block">
                      Luggage Count
                    </Label>
                    <Select value={formData.luggage} onValueChange={(value) => handleInputChange('luggage', value)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select luggage count" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">No Luggage</SelectItem>
                        <SelectItem value="1">1 Bag</SelectItem>
                        <SelectItem value="2">2 Bags</SelectItem>
                        <SelectItem value="3">3 Bags</SelectItem>
                        <SelectItem value="4">4 Bags</SelectItem>
                        <SelectItem value="5">5+ Bags</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <Label htmlFor="notes" className="text-base font-semibold mb-2 block">
                    Additional Notes / Flight Number
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Flight number, special requests, etc."
                    className="min-h-24"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full bg-[#00D4FF] hover:bg-[#00B8E6] text-black font-bold py-6 text-lg transition-all duration-200 hover:scale-105 shadow-xl hover:shadow-[#00D4FF]/50"
                >
                  Submit Booking Request
                </Button>

                <p className="text-sm text-gray-500 text-center mt-4">
                  A representative will contact you to confirm your booking.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};